import * as IF from "infrontjs";
import template from "./form.html";
import { validateScope, resetValidation, bindLiveClear } from "../general/form-validation";
import { _lcs } from "../../i18n";

/**
 * <client-user-form> — modal form to create or edit a client (Gastgeber) user.
 * Used by the ClientUser state (and reusable elsewhere).
 *
 * Usage:
 *   el.openCreate()        // create mode
 *   el.openEdit( user )    // edit mode
 *
 * On a successful save it dispatches a bubbling "client-user-saved" event
 * (detail: { mode, name }) so the host can refresh its list.
 */
class ClientUserForm extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
        this._mode = "create";
        this._id = null;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this.bind();
            this._connected = true;
        }
    }

    render()
    {
        this.app.view.render( this, template, {} );

        this._modal = this.querySelector( '[data-id="cuf-modal"]' );
        this._form = this.querySelector( '[data-id="cuf-form"]' );
        this._title = this.querySelector( '[data-id="cuf-title-text"]' );
        this._submit = this.querySelector( '[data-id="cuf-submit"]' );
        this._submitLabel = this.querySelector( '[data-id="cuf-submit-label"]' );
        this._error = this.querySelector( '[data-id="cuf-error"]' );
        this._passwordField = this.querySelector( "#cuf-password" );
    }

    bind()
    {
        this.querySelector( '[data-id="cuf-close"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="cuf-cancel"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="cuf-backdrop"]' ).addEventListener( "click", () => this.close() );
        this._form.addEventListener( "submit", ( e ) => this.onSubmit( e ) );

        // Clear a field's error as soon as the user edits it.
        bindLiveClear( this._form );

        this._onKeydown = ( e ) =>
        {
            if ( e.key === "Escape" && this._modal && !this._modal.hidden )
            {
                this.close();
            }
        };
        document.addEventListener( "keydown", this._onKeydown );
    }

    disconnectedCallback()
    {
        if ( this._onKeydown )
        {
            document.removeEventListener( "keydown", this._onKeydown );
        }
    }

    /* ------------------------------- Public API ------------------------------ */

    openCreate()
    {
        this._mode = "create";
        this._id = null;

        this._clearError();
        this._form.reset();
        this._title.textContent = _lcs( "Neuen Client anlegen" );
        this._submitLabel.textContent = _lcs( "Anlegen" );
        this._passwordField.required = true;
        this._passwordField.placeholder = _lcs( "Mindestens 6 Zeichen" );

        resetValidation( this._form );
        this._modal.hidden = false;
        this._field( "cuf-name" ).focus();
    }

    openEdit( user )
    {
        this._mode = "edit";
        this._id = user.id;

        this._clearError();
        this._form.reset();
        this._title.textContent = _lcs( "Client bearbeiten" );
        this._submitLabel.textContent = _lcs( "Aktualisieren" );
        // Password optional when editing (blank keeps the current one).
        this._passwordField.required = false;
        this._passwordField.placeholder = _lcs( "Leer lassen, um beizubehalten" );

        this._val( "cuf-name", ( user.data && user.data.name ) ? String( user.data.name ) : "" );
        this._val( "cuf-company", user.organisationName || "" );
        this._val( "cuf-email", user.email || "" );
        this._val( "cuf-status", user.status || "ok" );

        resetValidation( this._form );
        this._modal.hidden = false;
        this._field( "cuf-name" ).focus();
    }

    close()
    {
        this._modal.hidden = true;
        this._clearError();
        this._form.reset();
    }

    /* -------------------------------- Submit -------------------------------- */

    async onSubmit( e )
    {
        e.preventDefault();
        this._clearError();

        // Native constraint validation — marks invalid fields and shows hints.
        if ( !validateScope( this._form ) )
        {
            return;
        }

        const data = IF.Helper.serializeForm( this._form );
        const name = ( data.name || "" ).trim();
        const companyName = ( data.companyName || "" ).trim();
        const email = ( data.email || "" ).trim();
        const password = data.password || "";
        const status = data.status || "ok";
        const isEdit = this._mode === "edit";

        this._setSubmitting( true );
        try
        {
            if ( isEdit )
            {
                const payload = { name, email, status, companyName };
                if ( password ) payload.password = password;

                const { result } = await this.app.api.patch( "/admin/user/" + this._id, payload );
                if ( !result || +result.status !== 200 )
                {
                    throw new Error( "Unexpected update response." );
                }
                this.app.view.toast( _lcs( "Client-Benutzer „{name}“ wurde aktualisiert.", { name } ), "success" );
            }
            else
            {
                const { result } = await this.app.api.post( "/admin/user", { name, companyName, email, password, status, role: "client" } );
                if ( !result || +result.status !== 201 )
                {
                    throw new Error( "Unexpected create response." );
                }
                this.app.view.toast( _lcs( "Client-Benutzer „{name}“ wurde angelegt.", { name } ), "success" );
            }

            this.close();
            this.dispatchEvent( new CustomEvent( "client-user-saved", {
                bubbles: true,
                detail: { mode: this._mode, name }
            } ) );
        }
        catch ( err )
        {
            this._showError( this._saveErrorForStatus( +err?.status, isEdit ) );
        }
        finally
        {
            this._setSubmitting( false );
        }
    }

    /* ------------------------------- Helpers -------------------------------- */

    _field( id )
    {
        return this.querySelector( "#" + id );
    }

    _val( id, value )
    {
        const el = this._field( id );
        if ( value !== undefined )
        {
            if ( el ) el.value = value;
            return value;
        }
        return el ? el.value : "";
    }

    _saveErrorForStatus( status, isEdit )
    {
        switch ( status )
        {
            case 400: return _lcs( "Bitte überprüfe deine Eingaben." );
            case 401:
            case 403: return _lcs( "Keine Berechtigung für diese Aktion." );
            case 404: return _lcs( "Benutzer wurde nicht gefunden." );
            case 409: return _lcs( "Diese E-Mail-Adresse wird bereits verwendet." );
            default:  return isEdit
                ? _lcs( "Aktualisieren fehlgeschlagen. Bitte versuche es später erneut." )
                : _lcs( "Anlegen fehlgeschlagen. Bitte versuche es später erneut." );
        }
    }

    _setSubmitting( on )
    {
        if ( !this._submit ) return;
        this._submit.disabled = on;
        this._submit.setAttribute( "aria-busy", String( on ) );
    }

    _showError( message )
    {
        if ( !this._error ) return;
        this._error.textContent = message;
        this._error.hidden = false;
    }

    _clearError()
    {
        if ( !this._error ) return;
        this._error.textContent = "";
        this._error.hidden = true;
    }
}

customElements.define( "client-user-form", ClientUserForm );
